/**
 * Bedtime story library. 10 free Hindi regional myths. TTS-backed reader.
 * Premium 50+ stub locked behind Pro.
 */
import { STORIES, PREMIUM_STORY_COUNT, type BedtimeStory } from '../lib/stories.ts';
import { speak } from '../chassis/voice.ts';

const TPL = /* html */ `
<style>
  :host {
    display: block;
    margin: 28px 0;
    container-type: inline-size;
  }
  .card {
    border: 1px solid oklch(85% 0.02 248 / 0.12);
    border-radius: 14px;
    padding: 22px 24px;
    background: linear-gradient(180deg, oklch(11% 0.04 270 / 0.55), oklch(8% 0.03 268 / 0.65));
    box-shadow: 0 18px 40px -22px oklch(0% 0 0 / 0.85);
  }
  .eyebrow {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: oklch(70% 0.04 60);
    margin-bottom: 10px;
  }
  h3 {
    margin: 0 0 6px;
    font-family: var(--display);
    font-weight: 500;
    font-size: clamp(22px, 5vw, 30px);
    color: oklch(94% 0.04 88);
    letter-spacing: -0.01em;
  }
  .sub {
    font-family: var(--display);
    font-style: italic;
    color: oklch(72% 0.04 250);
    margin-bottom: 18px;
    font-size: 14px;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  .story {
    border: 1px solid oklch(80% 0.04 80 / 0.16);
    background: oklch(7% 0.02 270 / 0.4);
    border-radius: 12px;
    padding: 14px 16px;
    cursor: pointer;
    transition: transform 0.12s ease, border-color 0.12s ease;
  }
  .story:hover { transform: translateY(-1px); border-color: oklch(86% 0.14 82 / 0.5); }
  .story-mark {
    font-family: var(--hindi);
    font-size: 24px;
    color: oklch(91% 0.04 88);
    line-height: 1;
    margin-bottom: 6px;
  }
  .story-en {
    font-family: var(--display);
    font-style: italic;
    font-size: 13px;
    color: oklch(72% 0.04 250);
    margin-bottom: 8px;
  }
  .story-meta {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: oklch(64% 0.04 60);
  }
  dialog {
    background: oklch(9% 0.03 268);
    color: oklch(94% 0.04 88);
    border: 1px solid oklch(80% 0.04 80 / 0.2);
    border-radius: 14px;
    padding: 24px;
    max-width: 92vw;
    width: 480px;
    font-family: var(--display);
  }
  dialog::backdrop {
    background: oklch(0% 0 0 / 0.7);
  }
  dialog h4 {
    margin: 0 0 6px;
    font-family: var(--hindi);
    font-weight: 400;
    font-size: 30px;
  }
  dialog .body {
    font-style: italic;
    color: oklch(82% 0.05 60);
    line-height: 1.55;
    margin: 14px 0;
  }
  dialog .controls {
    display: flex;
    gap: 10px;
    margin-top: 16px;
  }
  dialog button {
    background: oklch(86% 0.14 82);
    color: oklch(12% 0.04 270);
    border: none;
    padding: 10px 18px;
    border-radius: 999px;
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    cursor: pointer;
  }
  dialog button.ghost {
    background: transparent;
    color: oklch(86% 0.04 80);
    border: 1px solid oklch(80% 0.04 80 / 0.25);
  }
  .upsell {
    margin-top: 16px;
    text-align: center;
    font-family: var(--display);
    font-style: italic;
    color: oklch(76% 0.06 60);
    font-size: 14px;
    padding: 14px;
    border: 1px dashed oklch(80% 0.04 80 / 0.22);
    border-radius: 10px;
  }
  @container (min-width: 520px) {
    .grid { grid-template-columns: repeat(3, 1fr); }
  }
</style>

<div class="card">
  <div class="eyebrow">Aramgah · Bedtime library · ${STORIES.length} free</div>
  <h3>Raat ki kahaaniyaan</h3>
  <div class="sub">Hindi audio · pārampārik shaili · headphone par soyiye</div>
  <div class="grid" id="grid"></div>
  <div class="upsell">Pro mein <strong>${PREMIUM_STORY_COUNT}+</strong> aur kahaaniyaan · ₹999/saal</div>
</div>
<dialog id="reader">
  <h4 id="r-title-hi">—</h4>
  <div id="r-title-en" class="story-en">—</div>
  <div id="r-meta" class="story-meta">—</div>
  <div id="r-body" class="body">—</div>
  <div class="controls">
    <button id="r-play">Sunaye</button>
    <button id="r-close" class="ghost">Band karein</button>
  </div>
</dialog>
`;

export class AramgahBedtimeStories extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    this.#renderGrid();
    const closeBtn = this.#shadow.getElementById('r-close');
    closeBtn?.addEventListener('click', () => {
      const d = this.#shadow.getElementById('reader') as HTMLDialogElement | null;
      d?.close();
    });
  }

  #renderGrid(): void {
    const grid = this.#shadow.getElementById('grid');
    if (!grid) return;
    grid.innerHTML = STORIES.map((s) => `
<div class="story" data-id="${s.id}">
  <div class="story-mark">${s.title_hi}</div>
  <div class="story-en">${s.title_en}</div>
  <div class="story-meta">${s.duration_min}m · ${s.tradition.replace(/_/g, ' ')}</div>
</div>`).join('');
    grid.querySelectorAll<HTMLElement>('.story').forEach((el) => {
      el.addEventListener('click', () => this.#open(el.dataset['id'] ?? ''));
    });
  }

  #open(id: string): void {
    const story = STORIES.find((s) => s.id === id);
    if (!story) return;
    const d = this.#shadow.getElementById('reader') as HTMLDialogElement | null;
    const setText = (i: string, v: string) => {
      const el = this.#shadow.getElementById(i);
      if (el) el.textContent = v;
    };
    setText('r-title-hi', story.title_hi);
    setText('r-title-en', story.title_en);
    setText('r-meta', `${story.duration_min}m · ${story.narrator_hi}`);
    setText('r-body', story.text_excerpt_hi);
    const play = this.#shadow.getElementById('r-play');
    if (play) {
      play.onclick = () => { void this.#play(story); };
    }
    d?.showModal();
  }

  async #play(story: BedtimeStory): Promise<void> {
    try {
      await speak(story.text_excerpt_hi, { lang: 'hi-IN', rate: 0.85, pitch: 0.95 });
    } catch {
      // ignore
    }
  }
}

customElements.define('aramgah-bedtime-stories', AramgahBedtimeStories);
